/**
 * DateInput: the house date box. A native `input type="date"` drawn as
 * SelectField draws its box (FIELD_BOX), at the same three sizes, so a
 * date beside a select on one line is the same height with the same edge:
 *   - `field`: the dialog form field;
 *   - `default`: the house input height;
 *   - `compact`: bands, drawers, filters and table rows.
 *
 * The input stays native: the browser's own picker, no portal, no key
 * handling. Every native prop passes through (`id`, `value`, `onChange`,
 * `min`, `max`, `aria-*`, `data-testid`, `ref`). The accessible name comes
 * from the caller's `<label htmlFor>` or `aria-label`.
 *
 * `className` is for placement only (`w-full`, `max-w-[11rem]`, `ml-2`).
 * Height, padding, border, radius and text belong here.
 *
 * The box never outgrows its container: `min-w-0` always, and
 * `max-w-full` unless the caller caps the width itself, so a native date
 * box whose own width grows with the reader's text size (200%) shrinks to
 * the column it sits in instead of pushing past a drawer's or a card's
 * edge. A caller's own cap (`w-full max-w-[11rem]`) replaces `max-w-full`
 * rather than sitting beside it: two max-width classes are decided by the
 * stylesheet's order, not the class list's, and `max-w-full` would win.
 */

import type { ComponentPropsWithRef, ReactNode } from 'react';
import { FIELD_BOX, type FieldBoxSize } from './fieldBox';

export interface DateInputProps extends Omit<ComponentPropsWithRef<'input'>, 'type' | 'size'> {
  size?: FieldBoxSize;
}

const PAD: Record<FieldBoxSize, string> = {
  field: 'px-[0.875rem]',
  default: 'px-3',
  compact: 'px-2.5',
};

/** True when the caller's classes set a max width of their own. */
function capsWidth(className: string | undefined): boolean {
  return className !== undefined && /(^|\s)max-w-/.test(className);
}

export function DateInput({ size = 'default', className, ...rest }: DateInputProps): ReactNode {
  return (
    <input
      {...rest}
      type="date"
      data-date-input=""
      className={`min-w-0${capsWidth(className) ? '' : ' max-w-full'} bg-paper text-ink disabled:cursor-not-allowed disabled:opacity-50 ${FIELD_BOX[size]} ${PAD[size]}${className ? ` ${className}` : ''}`}
    />
  );
}
